import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { Media } from '~/components';

import { selectableCheckbox } from '../../../selectable-item';
import { OCAB_QUESTION_TYPE, questionHelpers, SelectType } from '../../helpers';
import { Option } from './option';
import { ImageOption } from './image-option';

import stylesCommon from '../Question.scss';

const SelectableOption = selectableCheckbox(Option);
const SelectableImageOption = selectableCheckbox(ImageOption);

export class ChoiceQuestion extends PureComponent {
  constructor(props) {
    super(props);
    this.optionRef = React.createRef();
    this.state = {
      optionsListStyle: null,
    };
  }

  componentDidMount() {
    const { options, type, optionClassName } = this.props;

    if (type === OCAB_QUESTION_TYPE.IMAGE_SELECT_VERTICAL) {
      this.setState({
        optionsListStyle: {
          height: this.getOptionsListHeight(options, this.optionRef.current, optionClassName),
        },
      });
    }
  }

  onChange = ({ displayName, description }, event) => {
    const { selectType, onChange, value } = this.props;

    if (selectType === SelectType.MULTI) {
      const newValue = this.parse(value);
      if (!event.target.checked) {
        newValue.splice(newValue.indexOf(displayName), 1);
      } else {
        newValue.push(displayName);
      }
      onChange(newValue);
    } else {
      onChange(
        event.target.checked
          ? questionHelpers.composeAnswer({
              displayName,
              description,
            })
          : '',
      );
    }
  };

  parse = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  onBlurHandler = () => {
    const { value, selectType, onBlur } = this.props;

    onBlur(selectType === SelectType.MULTI ? this.parse(value) : value);
  };

  getSelectSize = (options) => {
    const NARROW_TEXT_LENGTH = 10;

    return options.some((option) => option.displayName.length > NARROW_TEXT_LENGTH)
      ? stylesCommon.NarrowSelectQuestion
      : '';
  };

  getOptionsListHeight = (options, option, optionClassName) => {
    const classNameContainsColumns = optionClassName.match(/[1-6]_col/);
    const columnsQuantity = classNameContainsColumns
      ? parseInt(classNameContainsColumns[0].match(/\d/)[0], 10)
      : 1;
    const marginTop = parseInt(window.getComputedStyle(option).getPropertyValue('margin-top'), 10);

    return `${Math.ceil(options.length / columnsQuantity) * (option && option.getBoundingClientRect().height + marginTop)}px`;
  };

  isOptionChecked(option) {
    const { value, selectType } = this.props;

    switch (true) {
      case questionHelpers.isComposedAnswer(value): {
        const [displayName] = questionHelpers.getComposedAnswer(value);
        return displayName === option.displayName;
      }
      case selectType === SelectType.MULTI: {
        return this.parse(value).indexOf(option.displayName) !== -1;
      }
      default: {
        return value === option.displayName;
      }
    }
  }

  render() {
    const { options, optionClassName, type } = this.props;
    const { optionsListStyle } = this.state;

    return (
      <Media>
        {({ screenWidth }) => (
          <ul
            className={`${stylesCommon.OptionsList} ${optionClassName}`}
            style={
              type === OCAB_QUESTION_TYPE.IMAGE_SELECT_VERTICAL &&
              Media.greaterThan(screenWidth, Media.BREAKPOINTS.XS)
                ? optionsListStyle
                : null
            }
          >
            {options.map((option) => {
              const checked = this.isOptionChecked(option);

              if (type && type.includes(OCAB_QUESTION_TYPE.IMAGE_SELECT)) {
                return (
                  <li key={option.id} ref={this.optionRef}>
                    <SelectableImageOption
                      {...option}
                      isChecked={checked}
                      onChange={(event) => this.onChange(option, event)}
                      onBlur={this.onBlurHandler}
                      data-testid="SelectableImageOption"
                    />
                  </li>
                );
              }
              return (
                <li key={option.id} className={`${this.getSelectSize(options)}`}>
                  <SelectableOption
                    {...option}
                    isChecked={checked}
                    onChange={(event) => this.onChange(option, event)}
                    onBlur={this.onBlurHandler}
                    data-testid="SelectableOption"
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Media>
    );
  }
}

ChoiceQuestion.propTypes = {
  type: PropTypes.string.isRequired,
  selectType: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  optionClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
};

ChoiceQuestion.defaultProps = {
  options: [],
  optionClassName: '',
  value: '[]',
};
